import { cloneElement, isValidElement, type RefObject } from 'react'
import { Button as ButtonPrimitive } from '@base-ui/react/button'
import { cva, type VariantProps } from 'class-variance-authority'
import { LoaderCircleIcon } from 'lucide-react'

import { cn } from '@/shared/lib/cn'

/**
 * Курсор-рука задан явно: Tailwind в четвёртой версии сбрасывает курсор кнопки на обычную
 * стрелку, поэтому без этого класса нажимаемый элемент выглядит как обычный текст.
 */
const buttonVariants = cva(
  "inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap [font-family:var(--control-font-family)] [border-radius:var(--control-button-radius)] [font-size:var(--control-button-font-size)] [font-weight:var(--control-button-font-weight)] [line-height:var(--control-button-line-height)] transition-[color,background-color,border-color,box-shadow,opacity] outline-none disabled:!cursor-not-allowed disabled:[border-color:var(--control-button-disabled-border)] disabled:[background-color:var(--control-button-disabled-background)] disabled:[color:var(--control-button-disabled-foreground)] disabled:[opacity:var(--control-button-disabled-opacity)] aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          '[border-width:var(--control-button-primary-border-width)] [border-color:var(--control-button-primary-border)] [background-color:var(--control-button-primary-background)] [color:var(--control-button-primary-foreground)] [box-shadow:var(--control-button-primary-shadow)] hover:[border-color:var(--control-button-primary-hover-border)] hover:[background-color:var(--control-button-primary-hover-background)] hover:[box-shadow:var(--control-button-interactive-shadow)] hover:[opacity:var(--control-button-primary-hover-opacity)] focus-visible:[border-color:var(--control-button-primary-hover-border)] focus-visible:[background-color:var(--control-button-primary-hover-background)] focus-visible:[box-shadow:var(--control-button-interactive-shadow)] active:[border-color:var(--control-button-primary-active-border)] active:[background-color:var(--control-button-primary-active-background)] active:[opacity:var(--control-button-primary-hover-opacity)]',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-destructive/20',
        'destructive-outline':
          'border [border-color:var(--control-button-destructive-outline-border)] [background-color:var(--control-button-destructive-outline-background)] [color:var(--control-button-destructive-outline-foreground)] hover:[border-color:var(--control-button-destructive-outline-hover-border)] hover:[background-color:var(--control-button-destructive-outline-hover-background)] hover:[color:var(--control-button-destructive-outline-hover-foreground)] hover:[box-shadow:var(--control-button-destructive-outline-interactive-shadow)] focus-visible:[border-color:var(--control-button-destructive-outline-hover-border)] focus-visible:[background-color:var(--control-button-destructive-outline-hover-background)] focus-visible:[color:var(--control-button-destructive-outline-hover-foreground)] focus-visible:[box-shadow:var(--control-button-destructive-outline-interactive-shadow)]',
        // Непрозрачный фон нужен на стыке карточки и фона страницы. Радиус задан произвольным
        // свойством: базовый радиус записан так же, и утилита слияния не считает rounded-full
        // конфликтующим с ним; тем же приёмом размер lg перекрывает базовый радиус строкой ниже.
        // Поэтому сочетание overlay-destructive с lg потеряет круглую форму.
        'overlay-destructive':
          'border [border-radius:var(--control-button-radius-round)] [background-color:var(--control-button-overlay-destructive-background)] [color:var(--control-button-overlay-destructive-foreground)] shadow-card hover:[border-color:var(--control-button-overlay-destructive-hover-border)] hover:[background-color:var(--control-button-overlay-destructive-hover-background)] hover:[color:var(--control-button-overlay-destructive-hover-foreground)] focus-visible:[border-color:var(--control-button-overlay-destructive-hover-border)] focus-visible:[background-color:var(--control-button-overlay-destructive-hover-background)] focus-visible:[color:var(--control-button-overlay-destructive-hover-foreground)] focus-visible:ring-2 focus-visible:ring-destructive/20',
        success:
          '[background-color:var(--control-button-success-background)] [color:var(--control-button-success-foreground)] hover:[background-color:var(--control-button-success-hover-background)] focus-visible:ring-2 focus-visible:ring-ring/20',
        warning:
          '[background-color:var(--control-button-warning-background)] [color:var(--control-button-warning-foreground)] hover:[background-color:var(--control-button-warning-hover-background)] focus-visible:ring-2 focus-visible:ring-ring/20',
        promo:
          '[background-image:var(--control-button-promo-background)] [color:var(--control-button-promo-foreground)] [box-shadow:var(--control-button-promo-shadow)] hover:[background-image:var(--control-button-promo-hover-background)] hover:[box-shadow:var(--control-button-promo-interactive-shadow)] focus-visible:[background-image:var(--control-button-promo-hover-background)] focus-visible:[box-shadow:var(--control-button-promo-interactive-shadow)] disabled:[background-image:none]',
        outline:
          'border [border-color:var(--control-button-outline-border)] [background-color:var(--control-button-outline-background)] [color:var(--control-button-outline-foreground)] [box-shadow:var(--control-button-outline-shadow)] hover:[border-color:var(--control-button-outline-hover-border)] hover:[background-color:var(--control-button-outline-hover-background)] hover:[color:var(--control-button-outline-hover-foreground)] hover:[box-shadow:var(--control-button-outline-interactive-shadow)] focus-visible:[border-color:var(--control-button-outline-hover-border)] focus-visible:[background-color:var(--control-button-outline-hover-background)] focus-visible:[color:var(--control-button-outline-hover-foreground)] focus-visible:[box-shadow:var(--control-button-outline-interactive-shadow)]',
        // Кнопка второго плана на тёмной поверхности (финальный призыв лендингов): обычная
        // outline там сливается с фоном, потому что нарисована светлой рамкой по белому.
        'outline-inverse':
          'border border-surface-inverse-foreground/28 bg-transparent text-surface-inverse-foreground hover:bg-surface-inverse-foreground/10 hover:border-surface-inverse-foreground/45 focus-visible:bg-surface-inverse-foreground/10 focus-visible:border-surface-inverse-foreground/45',
        secondary:
          '[background-color:var(--control-button-secondary-background)] [color:var(--control-button-secondary-foreground)] hover:[background-color:var(--control-button-secondary-hover-background)] focus-visible:ring-2 focus-visible:ring-ring/20',
        ghost: 'hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring/20',
        link: 'text-primary underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:ring-ring/20',
      },
      size: {
        default:
          '[height:var(--control-button-height)] [padding:var(--control-button-padding)] has-[>svg]:[padding-inline:var(--control-button-icon-padding-x)]',
        sm: 'gap-1.5 [height:var(--control-button-sm-height)] [padding:var(--control-button-sm-padding)] [font-size:var(--control-button-sm-font-size)] [line-height:var(--control-button-sm-line-height)] has-[>svg]:[padding-inline:var(--control-button-sm-icon-padding-x)]',
        lg: '[height:var(--control-button-lg-height)] [border-radius:var(--control-button-lg-radius)] [padding:var(--control-button-lg-padding)] has-[>svg]:[padding-inline:var(--control-button-lg-icon-padding-x)]',
        icon: 'size-9',
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

/**
 * Контуры значков lucide для кнопок, которые собираются без React. Компонент значка сюда не
 * подходит: у элемента, созданного вручную, нет места для React-узла, поэтому контуры скопированы
 * из lucide-react 0.556.0 (`__iconNode` в dist/esm/icons/*.js). Копии лежат вместе, чтобы при
 * обновлении пакета их было где искать: сам пакет не объявляет карту exports и не даёт типов на
 * отдельные значки, поэтому импортировать эти данные напрямую нельзя.
 */
const LOADER_ICON_PATHS = ['M21 12a9 9 0 1 1-6.219-8.56']
const BELL_ICON_PATHS = [
  'M10.268 21a2 2 0 0 0 3.464 0',
  'M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326',
]

/** Собирает значок lucide вручную — размер, толщина обводки и отсутствие заливки заданы жёстко. */
function createLucideSvg(paths: string[], className: string): SVGSVGElement {
  const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  icon.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
  icon.setAttribute('width', '24')
  icon.setAttribute('height', '24')
  icon.setAttribute('viewBox', '0 0 24 24')
  icon.setAttribute('fill', 'none')
  icon.setAttribute('stroke', 'currentColor')
  icon.setAttribute('stroke-width', '2')
  icon.setAttribute('stroke-linecap', 'round')
  icon.setAttribute('stroke-linejoin', 'round')
  icon.setAttribute('class', className)
  icon.setAttribute('aria-hidden', 'true')

  paths.forEach((pathData) => {
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    path.setAttribute('d', pathData)
    icon.append(path)
  })

  return icon
}

interface CreateButtonElementOptions {
  text: string
  variant: VariantProps<typeof buttonVariants>['variant']
  size: VariantProps<typeof buttonVariants>['size']
  /** Контуры значка слева от текста — берутся из констант этого файла, например BELL_ICON_PATHS. */
  iconPaths?: string[]
  pressed: boolean
  loading: boolean
  disabled: boolean
  ariaLabel: string
}

/** Возвращает готовую кнопку для ячеек таблицы, куда нельзя вставить React-компонент. */
function createButtonElement({
  text,
  variant,
  size,
  iconPaths,
  pressed,
  loading,
  disabled,
  ariaLabel,
}: CreateButtonElementOptions): HTMLButtonElement {
  const button = document.createElement('button')
  button.type = 'button'
  button.textContent = text
  button.className = buttonVariants({ variant, size })
  button.disabled = disabled || loading
  button.setAttribute('aria-pressed', String(pressed))
  button.setAttribute('aria-label', ariaLabel)

  // Пока идёт запрос, значок уступает место крутящемуся индикатору — иначе в кнопке было бы два значка.
  if (loading) {
    button.setAttribute('aria-busy', 'true')
    button.prepend(createLucideSvg(LOADER_ICON_PATHS, 'lucide lucide-loader-circle animate-spin size-4'))
  } else if (iconPaths) {
    button.prepend(createLucideSvg(iconPaths, 'size-4'))
  }

  return button
}

type ButtonProps = ButtonPrimitive.Props &
  VariantProps<typeof buttonVariants> & {
    loading?: boolean
    loadingText?: string
    ref?: RefObject<HTMLButtonElement | null>
  }

function Button({
  children,
  className,
  disabled,
  loading = false,
  loadingText,
  render,
  variant,
  size,
  ...props
}: ButtonProps) {
  const buttonClassName = cn(buttonVariants({ variant, size, className }))
  const content = (
    <>
      {loading && <LoaderCircleIcon aria-hidden="true" className="animate-spin" />}
      {loading && loadingText ? loadingText : children}
    </>
  )

  // Ссылку, оформленную как кнопка (render={<Link />}), нельзя отдавать Base UI:
  // он навесит ей role="button" и tabIndex, и для программы чтения с экрана
  // ссылка перестанет быть ссылкой.
  if (isValidElement<Record<string, unknown>>(render) && render.type !== 'button') {
    return cloneElement(
      render,
      { 'data-slot': 'button', ...props, className: cn(buttonClassName, render.props.className as string | undefined) },
      content
    )
  }

  return (
    <ButtonPrimitive
      aria-busy={loading || undefined}
      data-slot="button"
      disabled={disabled || loading}
      render={render}
      className={buttonClassName}
      {...props}
    >
      {content}
    </ButtonPrimitive>
  )
}

export { BELL_ICON_PATHS, Button, buttonVariants, createButtonElement }
